import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { GET_NOTIFICATIONS } from '../queries/common';
import { useAuthProvider } from './AuthProvider';
import { API_GQ, error } from '../helpers/apiConfig';
import { useApiError } from './ApiErrorProvider';
import { NotificationInterface } from '../models/app';

interface Props {
  children: ReactNode;
}

const NotificationsContext = createContext({
      notifications: [] as NotificationInterface[],
});

export const NotificationsProvider = ({children}: Props) => {   
      const authContext = useAuthProvider();
      const { setErrorCode } = useApiError();
      const [isDeviceOffline, setIsDeviceOffline] = useState(!navigator.onLine);
      const [notifications, setNotifications] = useState<NotificationInterface[]>([]);
      const setIsDeviceOfflineTrue = useCallback(() => setIsDeviceOffline(true), []);
      const setIsDeviceOfflineFalse = useCallback(() => setIsDeviceOffline(false), []);

      window.addEventListener('offline', setIsDeviceOfflineTrue);
      window.addEventListener('online', setIsDeviceOfflineFalse);

      const getNotifications = async () => {
            try {
                  const body = { 
                        query: GET_NOTIFICATIONS,
                        variables: {}
                  };
                  const accessToken = await authContext.acquireTokenSilently();
                  const response = await API_GQ.post(accessToken, body);
                  const data = await response.json();
                  const transformedNotifications = data.data.pactUserNotification.map((notification) => ({
                        ...notification,
                        notificationStartTime: Number(notification.notificationStartTime),
                        notificationEndTime: Number(notification.notificationEndTime)
                  }));
                  setNotifications(transformedNotifications);
            } catch (e: any) {
                  setErrorCode(error.DEFAULT);
            }
      };
      
      useEffect(() => {
            if(!authContext.isAuthenticated) return;
            getNotifications();
            return () => {
                  window.removeEventListener('offline', setIsDeviceOfflineTrue);
                  window.removeEventListener('online', setIsDeviceOfflineFalse);
            }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [authContext.isAuthenticated]);

      useEffect(() => {
            let interval: NodeJS.Timeout | null = null;
            if(isDeviceOffline || !authContext.isAuthenticated) {
              interval !== null && clearInterval(interval);
              return;
            }
            interval = setInterval(() => {
                getNotifications();
            // }, 3 * 1000);
            // Fetch notifications every 30 minutes
            }, 30 * 60 * 1000);
            return () => {
                interval !== null && clearInterval(interval);
            };
          // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [isDeviceOffline, authContext.isAuthenticated]);
  
      return (
            <NotificationsContext.Provider 
                  value={{ 
                        notifications
                  }}
            >
                  {children}
            </NotificationsContext.Provider>
      );
};
  
export const useNotifications = () => {
  return useContext(NotificationsContext);
};
